import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { editFormData } from './edit-form-data';
import { falconClient } from '@/data/@clients';
import { clientKeys } from '@/data/@clients/client-keys';
import { baseUrl } from '@/data/learning-path/base';
import type {
  CreatePathInput,
  CreatePathResponse,
  EditPathInput,
  ManageLearningPathsResponse,
  PathResponse,
} from '@/data/learning-path/interfaces';
import { useBootstrapData } from '@/data/user/bootstrap-data';

export const useManagePathsIndex = () => {
  const { bootstrap } = useBootstrapData();
  const companyId = bootstrap?.info?.colaborador?.empresa_id;

  const response = useQuery<ManageLearningPathsResponse>({
    queryKey: [clientKeys.useManagePathsIndex, companyId],
    queryFn: async () =>
      await falconClient.get(`${baseUrl}/manage/paths/company/${companyId}`),
  });

  return {
    globalPaths: response?.data?.learning_paths?.global_paths ?? [],
    companyPaths: response?.data?.learning_paths?.company_paths ?? [],
    ...response,
  };
};

export const useShowPathManagement = ({ pathId }: { pathId: string }) => {
  const response = useQuery<PathResponse>({
    queryKey: [clientKeys.useShowPathManagement, pathId],
    queryFn: async () =>
      await falconClient.get(`${baseUrl}/manage/paths/${pathId}`),
  });

  const path = response?.data?.learning_path;
  return { path, ...response };
};

export const useCreate = () => {
  const queryClient = useQueryClient();
  return useMutation<CreatePathResponse, Error, CreatePathInput>({
    mutationFn: async (data) => {
      return await falconClient.post(`${baseUrl}/manage/paths`, data);
    },
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: [clientKeys.useManagePathsIndex],
      });
      queryClient.refetchQueries({
        queryKey: [clientKeys.useShowPathManagement],
      });
      queryClient.refetchQueries({
        queryKey: [clientKeys.usePathsIndex],
      });
      queryClient.refetchQueries({
        queryKey: [clientKeys.usePathsByScope],
      });
    },
  });
};

export const useEdit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: EditPathInput) => {
      const formData = editFormData(data);

      return await falconClient.put(
        `${baseUrl}/manage/paths/${data.id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
    },
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: [clientKeys.useManagePathsIndex],
      });
      queryClient.refetchQueries({
        queryKey: [clientKeys.useShowPathManagement],
      });
      queryClient.refetchQueries({
        queryKey: [clientKeys.usePathsIndex],
      });
      queryClient.refetchQueries({
        queryKey: [clientKeys.usePathsByScope],
      });
    },
  });
};

export const useDelete = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (pathId: string) => {
      return await falconClient.delete(`${baseUrl}/manage/paths/${pathId}`);
    },
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: [clientKeys.useManagePathsIndex],
        type: 'all',
      });
      queryClient.refetchQueries({
        queryKey: [clientKeys.useShowPathManagement],
      });
      queryClient.refetchQueries({
        queryKey: [clientKeys.usePathsIndex],
      });
      queryClient.refetchQueries({
        queryKey: [clientKeys.usePathsByScope],
      });
    },
  });
};

export const usePublish = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ pathId }: { pathId: string }) => {
      return await falconClient.patch(
        `${baseUrl}/manage/paths/${pathId}/publish`
      );
    },
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: [clientKeys.useManagePathsIndex],
      });
      queryClient.refetchQueries({
        queryKey: [clientKeys.useShowPathManagement],
      });
      queryClient.refetchQueries({
        queryKey: [clientKeys.usePathsIndex],
      });
      queryClient.refetchQueries({
        queryKey: [clientKeys.usePathsByScope],
      });
    },
  });
};

export const useVisibility = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ pathId }: { pathId: string }) => {
      return await falconClient.patch(
        `${baseUrl}/manage/paths/${pathId}/visibility`
      );
    },
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: [clientKeys.useManagePathsIndex],
      });
      queryClient.refetchQueries({
        queryKey: [clientKeys.usePathsIndex],
      });
      queryClient.refetchQueries({
        queryKey: [clientKeys.usePathsByScope],
      });
    },
  });
};

export const useReorderPaths = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      scope,
      paths,
    }: {
      scope: string;
      paths: { id: string }[];
    }) => {
      return await falconClient.patch(`${baseUrl}/manage/paths/order`, {
        scope,
        paths,
      });
    },
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: [clientKeys.useManagePathsIndex],
      });
      queryClient.refetchQueries({
        queryKey: [clientKeys.usePathsByScope],
      });
      queryClient.refetchQueries({
        queryKey: [clientKeys.usePathsIndex],
      });
    },
  });
};

export const useDiscardDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ pathId }: { pathId: string }) => {
      return await falconClient.delete(
        `${baseUrl}/manage/paths/${pathId}/draft`
      );
    },
    onSuccess: (_, { pathId }) => {
      queryClient.refetchQueries({
        queryKey: [clientKeys.useManagePathsIndex],
      });
      queryClient.refetchQueries({
        queryKey: [clientKeys.useShowPathManagement, pathId],
      });
      queryClient.refetchQueries({
        queryKey: [clientKeys.usePathsIndex],
      });
      queryClient.refetchQueries({
        queryKey: [clientKeys.usePathsByScope],
      });
    },
  });
};
